/**
 * Helper functions to parse the search combined config response. Ideally
 * this file should exist in the search-client package, however there are
 * too many 3P types defined in search-page.
 */

import { type NounKeys, nouns } from '../../../common/constants/nouns';
import { allProductKeys, ProductKeys } from '../../../common/constants/products';
import type { ThirdPartyConfigsWithAPIResponse } from '../../../common/constants/schemas/3p-config';
import type { SearchCombinedConfigPayload } from '../../../common/types/search-combined-config';
import { getAccessibleProducts as getSupportedAccessibleProducts } from '../../../common/utils/get-accessible-products';
import { adaptCombinedConfigToLegacy } from '../combined-config-adapter';

import { map3pResourcesToProductKeys } from './third-party-products';

type ParsingArguments = {
	configuration: SearchCombinedConfigPayload;
	primaryProduct: string; // TODO: Extract PrimaryProductKey into common location
	cloudId: string;
};

export const getAccessibleNouns = ({ configuration }: ParsingArguments): NounKeys[] => {
	if (configuration.firstPartyProducts) {
		const firstPartyProducts = configuration.firstPartyProducts.map((product) => product.name);
		const accessibleNouns = firstPartyProducts.filter((product) => product in nouns) as NounKeys[];
		return accessibleNouns;
	} else {
		return [];
	}
};

export const getAccessibleProducts = ({
	configuration,
	primaryProduct,
	cloudId,
}: ParsingArguments): string[] => {
	let accessibleProducts = [];

	// TODO: QS-9614 Stop having to convert back to legacy 1P/3P config types
	const { thirdPartyConfig } = adaptCombinedConfigToLegacy(configuration, cloudId);

	// Add first party products to the array
	if (configuration.firstPartyProducts) {
		const firstPartyProducts = configuration.firstPartyProducts
			.map((product) => product.name)
			.filter((product) => allProductKeys.includes(product));
		accessibleProducts.push(...firstPartyProducts);
	}

	// Add Atlas to the array if there is more than one accessible noun
	const accessibleNouns = getAccessibleNouns({ configuration, primaryProduct, cloudId });
	if (accessibleNouns.length > 1) {
		accessibleProducts.push(ProductKeys.Atlas);
	}

	// Add third party products to the array
	if (configuration?.siteMetadata?.isRovoEnabled) {
		const { productKeys: thirdPartyProductKeys } = map3pResourcesToProductKeys(thirdPartyConfig);
		accessibleProducts.push(...thirdPartyProductKeys);
	}

	// Ensure primary product is always in the array
	if (!accessibleProducts.includes(primaryProduct)) {
		accessibleProducts.unshift(primaryProduct);
	}

	return accessibleProducts;
};

export const getProductDataRegions = ({
	configuration,
}: ParsingArguments): Record<string, string[]> | undefined => {
	if (configuration?.siteMetadata?.productInfo?.length) {
		const entries = configuration.siteMetadata.productInfo.reduce<[string, string[]][]>(
			(acc, product) => {
				if (product.name) {
					const dataRegion = Array.isArray(product.dataRegion)
						? product.dataRegion
						: typeof product.dataRegion === 'string'
							? [product.dataRegion]
							: [];
					acc.push([product.name, dataRegion]);
				}
				return acc;
			},
			[],
		);
		return Object.fromEntries(entries);
	} else {
		return undefined;
	}
};

export const getProductEdition = ({
	configuration,
}: ParsingArguments):
	| {
			confluence: string;
	  }
	| undefined => {
	const productEdition = configuration?.siteMetadata?.productEdition;
	if (typeof productEdition === 'string' && productEdition) {
		return {
			confluence: productEdition,
		};
	} else {
		return undefined;
	}
};

export const getThirdPartyConfigs = ({
	configuration,
	cloudId,
}: ParsingArguments): Partial<Record<string, ThirdPartyConfigsWithAPIResponse>> => {
	if (configuration?.siteMetadata?.isRovoEnabled) {
		// TODO: QS-9614 Stop having to convert back to legacy 1P/3P config types
		return map3pResourcesToProductKeys(
			adaptCombinedConfigToLegacy(configuration, cloudId).thirdPartyConfig,
		).thirdPartyConfigs;
	} else {
		return {};
	}
};

/**
 * Assumptions:
 * - rovo-search-combined-loom-auth FG is cleaned up
 * - rovo-search-patch-primary-product FG is cleaned up
 * - Bitbucket access is handled in the backend, i.e. it will not show up
 *   in firstPartyProducts if the user does not have access to Bitbucket
 * - nouns are defined in first party products, checked with Kamil that
 *   this is valid as the legacy behaviour has been cleaned up
 * - rovoFeatures does not exist, there are no usages in the codebase anyway
 */
export const parseCombinedConfig = (
	args: ParsingArguments,
): {
	accessibleNouns: NounKeys[];
	accessibleProducts: string[];
	intercomHmac: string | undefined;
	isJiraIssueIndexed: boolean;
	isRovoEnabled: boolean;
	productDataRegions: Record<string, string[]> | undefined;
	productEdition:
		| {
				confluence: string;
		  }
		| undefined;
	rovoFeatures: undefined;
	thirdPartyConfigs: Partial<Record<string, ThirdPartyConfigsWithAPIResponse>>;
	supportedAccessibleProducts: string[];
} => {
	const { configuration } = args;

	const accessibleProducts = getAccessibleProducts(args);

	return {
		accessibleNouns: getAccessibleNouns(args),
		accessibleProducts,
		intercomHmac: configuration?.intercomHmac ?? undefined,
		isJiraIssueIndexed: Boolean(configuration?.siteMetadata?.isJiraIssueIndexed),
		isRovoEnabled: Boolean(configuration?.siteMetadata?.isRovoEnabled),
		productDataRegions: getProductDataRegions(args),
		productEdition: getProductEdition(args),
		rovoFeatures: undefined,
		thirdPartyConfigs: getThirdPartyConfigs(args),
		supportedAccessibleProducts: getSupportedAccessibleProducts({
			supportedProducts: allProductKeys,
			accessibleProducts,
		}),
	};
};
